import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PlaceIcon from '@mui/icons-material/Place'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import {blue, grey} from '@mui/material/colors'
import {IBusiness} from 'app/modules/discovery/core'
import {Link} from 'react-router-dom'
import {useTracking} from 'react-tracking'
import {useStylesBusinessSearchResult} from 'styles/useStyles'
import {emGetInitials} from 'utilities/extensionMethods'
import FavoriteButton from '../FavoriteButton'
import HorizontalSquareChips from '../HorizontalSquareChips'
import ShowMoreText from '../ShowMoreText'
import AwardIcon from '../icons/AwardIcon'
import SamIcon from '../icons/SamIcon'

export default function BusinessResultCard({
  bus,
  currScrollPosition,
  search,
  fromDirectory,
  fromProject,
  page,
  showFullBusiness,
  showSmaller,
}: {
  bus: IBusiness
  currScrollPosition?: number
  search?: string
  fromDirectory?: boolean
  fromProject?: boolean
  page?: number
  showFullBusiness?: (businessId: string) => void
  showSmaller?: boolean
}) {
  const {trackEvent} = useTracking<{entity: string; id: string}>()
  const classes = useStylesBusinessSearchResult()

  return (
    <Box display={'flex'}>
      <Box flex={'1 1 80%'} alignItems={'baseline'}>
        <Box
          sx={{
            flex: '1 1 auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              fontSize: '22px',
              alignItems: 'center',
              fontWeight: '500',
            }}
            gap={1}
          >
            <Box flex={'0 0 auto'}>
              <Box className={classes.imgContainer}>
                {bus?.logo ? (
                  <img className={classes.img} src={`${bus?.logo}`} loading={'lazy'} />
                ) : (
                  <span style={{fontSize: 12}}>{bus.name ? emGetInitials(bus.name, 4) : ''}</span>
                )}
              </Box>
            </Box>
            {fromDirectory ? (
              <Link
                className='text-hover-primary'
                to={`/businesses/detail?externalId=${
                  bus.id
                }&scrollPosition=${currScrollPosition}&fromBusinessDirectory=${
                  search !== '' ? search : 'tru'
                }${page ? `&page=${page}` : ''}`}
                onClick={() => {
                  trackEvent({entity: 'business', id: `${bus.id}`})
                }}
              >
                {bus?.name}
              </Link>
            ) : fromProject && showFullBusiness ? (
              <Button onClick={() => bus.id && showFullBusiness(bus.id)}>
                {' '}
                <Typography color={blue[500]} variant={showSmaller ? 'body1' : 'h6'}>
                  {bus?.name}
                </Typography>
              </Button>
            ) : (
              <Link className='text-hover-primary' to={`/businesses/detail?externalId=${bus.id}`}>
                {bus?.name}
              </Link>
            )}

            {/* for city,state */}
            {bus?.city && bus?.state_province && (
              <>
                <PlaceIcon fontSize='small' sx={{marginLeft: 2, color: 'gray'}} />
                <Typography variant={showSmaller ? 'body1' : 'h6'}>
                  {bus?.city}, {bus?.state_province}
                </Typography>
                <Typography variant={showSmaller ? 'body1' : 'h6'} ml={2} color={grey[600]}>
                  {bus.country ? bus.country : ''}{' '}
                </Typography>
              </>
            )}
            {/* industries */}
            {!showSmaller && (
              <Box ml={10} color={'ButtonFace'}>
                <HorizontalSquareChips
                  items={(bus?.industries as string[]) ?? []}
                  size={'small'}
                  maxItems={8}
                  moreString='Show All Industries'
                  lessString='Show Less Industries'
                />
              </Box>
            )}
          </Box>
          <Box ml={5}>
            <ShowMoreText
              variant={showSmaller ? 'body1' : 'h6'}
              maxLen={300}
              text={bus?.description ?? ''}
              emptyCondition={'no description'}
              moreString='...Show More'
              lessString='...Show Less'
            />
          </Box>
        </Box>
      </Box>
      <Box flex={'1 1 20%'}>
        <Box display={'flex'} justifyContent={'flex-start'} alignItems={'center'} gap={0}>
          <FavoriteButton item={bus} type={'businesses'} />
          {bus?.crunchbase_active && (
            <Tooltip title={'Company Active'}>
              <Box display={'flex'}>
                <CheckCircleIcon fontSize={'medium'} color={'success'} />
              </Box>
            </Tooltip>
          )}

          {bus.awards_count && bus.awards_count > 0 ? (
            <Box p={2}>
              <AwardIcon width='400' height='400' fs='medium' />
              &nbsp;
              {bus.awards_count ? bus.awards_count : undefined}
            </Box>
          ) : (
            <></>
          )}

          {bus.sam_identifier && (
            <SamIcon width={'415'} height={'85'} fs={'large'} scale={2.0} m={2} />
          )}
        </Box>
      </Box>
    </Box>
  )
}
